import { useState } from "react";
import { cn } from "../utils/cn";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface StarRatingProps {
  value: number;
  max?: number;
  count?: number;
  size?: "sm" | "md" | "lg";
  interactive?: boolean;
  onChange?: (value: number) => void;
  className?: string;
}

// ── Size tokens ───────────────────────────────────────────────────────────────

const SIZE_PX = { sm: 13, md: 17, lg: 22 };
const COUNT_TEXT = { sm: "text-xs", md: "text-sm", lg: "text-base" };
const GAP = { sm: "gap-0.5", md: "gap-1", lg: "gap-1.5" };

// ── Star shape ────────────────────────────────────────────────────────────────

type StarFill = "full" | "half" | "empty";

function StarSvg({ px, className }: { px: number; className?: string }) {
  return (
    <svg
      width={px}
      height={px}
      viewBox="0 0 24 24"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="0.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Star({ fill, px }: { fill: StarFill; px: number }) {
  if (fill === "full") {
    return (
      <span className="inline-flex text-amber-400 dark:text-amber-400">
        <StarSvg px={px} />
      </span>
    );
  }

  if (fill === "empty") {
    return (
      <span className="inline-flex text-gray-200 dark:text-zinc-500">
        <StarSvg px={px} />
      </span>
    );
  }

  // Half star: overlay an amber star clipped to left 50%
  return (
    <span className="relative inline-flex">
      <span className="text-gray-200 dark:text-zinc-500">
        <StarSvg px={px} />
      </span>
      <span className="absolute inset-0 overflow-hidden w-1/2 text-amber-400 dark:text-amber-400">
        <StarSvg px={px} />
      </span>
    </span>
  );
}

function getStarFill(starIndex: number, value: number): StarFill {
  if (value >= starIndex) return "full";
  if (value >= starIndex - 0.5) return "half";
  return "empty";
}

// ── Component ─────────────────────────────────────────────────────────────────

export function StarRating({
  value,
  max = 5,
  count,
  size = "md",
  interactive = false,
  onChange,
  className,
}: StarRatingProps) {
  const [hoverValue, setHoverValue] = useState<number | null>(null);

  const displayValue = interactive && hoverValue !== null ? hoverValue : value;
  const px = SIZE_PX[size];

  const handleMouseMove = (e: React.MouseEvent<HTMLButtonElement>, starIndex: number) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    setHoverValue(x < rect.width / 2 ? starIndex - 0.5 : starIndex);
  };

  const handleClick = () => {
    if (hoverValue !== null) onChange?.(hoverValue);
  };

  const formatCount = (n: number): string =>
    n >= 1000 ? `${(n / 1000).toFixed(1).replace(".0", "")}k` : String(n);

  return (
    <div
      className={cn("inline-flex items-center", GAP[size], className)}
      role={interactive ? "group" : undefined}
      aria-label={`Rating: ${value} out of ${max}${count !== undefined ? `, ${count} reviews` : ""}`}
    >
      {Array.from({ length: max }).map((_, i) => {
        const starIndex = i + 1;
        const fill = getStarFill(starIndex, displayValue);

        if (!interactive) {
          return <Star key={i} fill={fill} px={px} />;
        }

        return (
          <button
            key={i}
            type="button"
            aria-label={`Rate ${starIndex} star${starIndex !== 1 ? "s" : ""}`}
            onMouseMove={(e) => handleMouseMove(e, starIndex)}
            onMouseLeave={() => setHoverValue(null)}
            onClick={handleClick}
            className={cn(
              "inline-flex cursor-pointer",
              "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand-purple rounded-sm",
              "transition-transform duration-100 hover:scale-110",
            )}
          >
            <Star fill={fill} px={px} />
          </button>
        );
      })}

      {/* Numeric value (display mode) */}
      {!interactive && value > 0 && (
        <span className={cn("font-semibold text-content-primary tabular-nums", COUNT_TEXT[size])}>
          {value % 1 === 0 ? value.toFixed(1) : value}
        </span>
      )}

      {/* Review count */}
      {count !== undefined && (
        <span className={cn("text-content-tertiary", COUNT_TEXT[size])}>
          ({formatCount(count)})
        </span>
      )}
    </div>
  );
}

StarRating.displayName = "StarRating";
